import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FileText, Download, Wallet } from "lucide-react";
import { formatDT } from "@/lib/format";

// Billing History card — sits low on the Settings page. Shows the last 12
// Stripe invoices with status, amount, and direct links to the hosted
// invoice + PDF. Users without a Stripe customer (trial / free with no
// prior subscription) see nothing — we hide the whole section rather than
// render an empty shell.
export const BillingHistoryCard = () => {
  const [invoices, setInvoices] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/subscription/invoices");
        if (!cancelled) setInvoices(data.invoices || []);
      } catch (_err) {
        if (!cancelled) setInvoices([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hide entirely if the user has no Stripe history at all.
  if (!invoices || invoices.length === 0) return null;

  return (
    <section className="border border-[#D9E1E3] bg-white rounded-md p-6 space-y-4" data-testid="billing-history-card">
      <div className="flex items-start gap-3">
        <span className="w-8 h-8 rounded-sm bg-[#EEF3F4] text-[#6F828A] inline-flex items-center justify-center flex-shrink-0 mt-0.5">
          <Wallet className="w-4 h-4" strokeWidth={1.5} />
        </span>
        <div>
          <h2 className="font-heading text-lg font-medium">Billing history</h2>
          <p className="text-sm text-[#6F828A] mt-1">Last {invoices.length} invoice{invoices.length === 1 ? "" : "s"} from Stripe.</p>
        </div>
      </div>

      <ul className="divide-y divide-[#EEF3F4] border border-[#D9E1E3] rounded-md" data-testid="billing-history-list">
        {invoices.map((inv) => (
          <li key={inv.id} className="p-3 flex items-center gap-3 flex-wrap sm:flex-nowrap" data-testid={`billing-history-row-${inv.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-mono-data text-[#2E3A3F] text-sm">{inv.number || inv.id}</span>
                <StatusBadge status={inv.status} />
              </div>
              <div className="text-xs text-[#6F828A] mt-0.5">{inv.created_at ? formatDT(inv.created_at) : "—"}</div>
            </div>
            <div className="font-mono-data tabular-nums text-[#2E3A3F] text-sm w-20 text-right">
              €{Number(inv.amount_paid || inv.amount_due || 0).toFixed(2)}
            </div>
            <div className="flex items-center gap-3 text-xs">
              {inv.hosted_invoice_url && (
                <a
                  href={inv.hosted_invoice_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[#116B6E] hover:underline"
                  data-testid={`billing-history-view-${inv.id}`}
                >
                  <FileText className="w-3.5 h-3.5" /> View
                </a>
              )}
              {inv.invoice_pdf && (
                <a
                  href={inv.invoice_pdf}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[#116B6E] hover:underline"
                  data-testid={`billing-history-pdf-${inv.id}`}
                >
                  <Download className="w-3.5 h-3.5" /> PDF
                </a>
              )}
            </div>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-[#6F828A] italic">Invoices are generated and stored by Stripe. Links open in a new tab.</p>
    </section>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    paid:      { bg: "bg-[#DCEFDC]", fg: "text-[#1E5A1E]", border: "border-[#A9D3A9]", label: "Paid" },
    open:      { bg: "bg-[#FDF4E2]", fg: "text-[#8F6B1F]", border: "border-[#E6CFA2]", label: "Open" },
    uncollectible: { bg: "bg-[#FBEFED]", fg: "text-[#8C2A22]", border: "border-[#E8B8B2]", label: "Uncollectible" },
    void:      { bg: "bg-[#F0EFEE]", fg: "text-[#6F828A]", border: "border-[#CFD6D8]", label: "Void" },
    draft:     { bg: "bg-[#F0EFEE]", fg: "text-[#6F828A]", border: "border-[#CFD6D8]", label: "Draft" },
  };
  const s = map[status] || map.draft;
  return (
    <span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border ${s.bg} ${s.fg} ${s.border}`}>
      {s.label}
    </span>
  );
};
